import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearMessage } from '../../../reducers/message';
import { completePasswordReset } from '../../../services/auth';
import LiquidGlassCard from '../LiquidGlassCard';
import '../LoginCard/index.css';
import '../AuthSupportCard.css';

const RESET_TOKEN_MAX_LENGTH = 512;
const PASSWORD_MAX_LENGTH = 256;
const INVALID_TOKEN_MESSAGE = 'Invalid or expired reset token';
const PASSWORD_TOO_SHORT_MESSAGE = 'Password must be at least 8 characters.';
const PASSWORD_BLANK_MESSAGE = 'Password cannot be blank.';
const PASSWORD_REUSED_MESSAGE = 'New password must be different from current password';

const ResetPasswordCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isTokenRejected, setIsTokenRejected] = useState(false);

  useEffect(() => {
    dispatch(clearMessage());
    return () => {
      dispatch(clearMessage());
    };
  }, [dispatch]);

  const token = useMemo(() => {
    const rawToken = searchParams.get('token');
    return typeof rawToken === 'string' ? rawToken.trim() : '';
  }, [searchParams]);

  useEffect(() => {
    setIsTokenRejected(false);
    setError('');
  }, [token]);

  const hasToken = token.length > 0 && token.length <= RESET_TOKEN_MAX_LENGTH;
  const hasUsableToken = hasToken && !isTokenRejected;

  const getValidationMessage = () => {
    if (!hasToken) {
      return t('auth.passwordReset.complete.feedback.invalidToken');
    }
    if (!form.newPassword || !form.confirmPassword) {
      return t('auth.passwordReset.complete.validation.missingFields');
    }
    if (!form.newPassword.trim()) {
      return t('auth.passwordReset.complete.validation.blank');
    }
    if (form.newPassword.length < 8) {
      return t('auth.passwordReset.complete.validation.length');
    }
    if (form.newPassword !== form.confirmPassword) {
      return t('auth.passwordReset.complete.validation.mismatch');
    }
    return null;
  };

  const mapServerMessage = (serverMessage) => {
    if (serverMessage === INVALID_TOKEN_MESSAGE) {
      setIsTokenRejected(true);
      return t('auth.passwordReset.complete.feedback.invalidToken');
    }
    if (serverMessage === PASSWORD_TOO_SHORT_MESSAGE) {
      return t('auth.passwordReset.complete.validation.length');
    }
    if (serverMessage === PASSWORD_BLANK_MESSAGE) {
      return t('auth.passwordReset.complete.validation.blank');
    }
    if (serverMessage === PASSWORD_REUSED_MESSAGE) {
      return t('auth.passwordReset.complete.validation.sameAsCurrent');
    }
    return t('auth.passwordReset.complete.feedback.genericError');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationMessage = getValidationMessage();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await dispatch(completePasswordReset(token, form.newPassword));
      navigate('/login', {
        replace: true,
        state: {
          authFlashMessageKey: 'auth.passwordReset.complete.feedback.success',
        },
      });
    } catch (errorResponse) {
      setError(mapServerMessage(errorResponse?.response?.data?.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LiquidGlassCard
      title={t('auth.passwordReset.complete.title')}
      variant="auth"
      size="medium"
      className="login-card"
    >
      {hasUsableToken ? (
        <form onSubmit={handleSubmit} className="login-form" noValidate>
          <p className="auth-support-copy">
            {t('auth.passwordReset.complete.descriptions.start')}
          </p>

          {error && (
            <div className="auth-status auth-status--error" role="alert">
              {error}
            </div>
          )}

          <div className="form-group">
            <input
              type="password"
              className="form-control"
              name="newPassword"
              placeholder={t('auth.passwordReset.complete.fields.newPassword.placeholder')}
              aria-label={t('auth.passwordReset.complete.fields.newPassword.placeholder')}
              autoComplete="new-password"
              maxLength={PASSWORD_MAX_LENGTH}
              value={form.newPassword}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <input
              type="password"
              className="form-control"
              name="confirmPassword"
              placeholder={t('auth.passwordReset.complete.fields.confirmPassword.placeholder')}
              aria-label={t('auth.passwordReset.complete.fields.confirmPassword.placeholder')}
              autoComplete="new-password"
              maxLength={PASSWORD_MAX_LENGTH}
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <p className="auth-form-note">
            {t('auth.passwordReset.complete.help')}
          </p>

          <div className="form-group">
            <button className="btn login-btn" disabled={isSubmitting}>
              {isSubmitting && <span className="spinner-border spinner-border-sm"></span>}
              <span>
                {isSubmitting
                  ? t('auth.passwordReset.complete.actions.submitting')
                  : t('auth.passwordReset.complete.actions.submit')}
              </span>
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="auth-status auth-status--error" role="alert">
            {t('auth.passwordReset.complete.feedback.invalidToken')}
          </div>
          <p className="auth-support-copy">
            {t('auth.passwordReset.complete.descriptions.invalidToken')}
          </p>
          <div className="auth-secondary-actions">
            <Link className="auth-secondary-button" to="/forgot-password">
              {t('auth.passwordReset.complete.actions.requestNewLink')}
            </Link>
          </div>
        </>
      )}

      <p className="login-footer">
        {t('auth.passwordReset.complete.footer.prompt')}{' '}
        <Link to="/login">{t('auth.passwordReset.complete.footer.link')}</Link>
      </p>
    </LiquidGlassCard>
  );
};

export default ResetPasswordCard;
